import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { S } from "./styles";
import { getConfig, buildWhatsappLink, titleCase, openWhatsApp } from "@/lib/ibloomConfig";

const SLOT_TIMES = ["10:00", "16:00", "18:00"];
const nextDays = (n) => Array.from({ length: n }, (_, i) => {
  const d = new Date();
  d.setDate(d.getDate() + i + 1);
  return d.toISOString().slice(0, 10);
});

/**
 * One-tap booking modal for the Free Essentials Check (BP · Sugar · BMI · Oxygen).
 * Deducts 499 reward points via /bookings/home-check and pings Kirit on WhatsApp
 * so he can confirm the slot personally. Designed for live-camp demo use:
 * minimal fields, auto-picks first family member, sane defaults.
 */
export default function EssentialsCheckBookModal({ user, members = [], onClose, onBooked, showToast }) {
  const days = useMemo(() => nextDays(3), []);
  const [memberId, setMemberId] = useState(members[0]?.id || "");
  const [day, setDay] = useState(days[0]);
  const [time, setTime] = useState(SLOT_TIMES[1]);
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState(user?.mobile || "");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [config, setConfig] = useState(null);

  useEffect(() => { getConfig().then(setConfig); }, []);
  useEffect(() => { if (!memberId && members[0]?.id) setMemberId(members[0].id); }, [members, memberId]);

  const member = members.find((m) => m.id === memberId);
  const slot = `${day}T${time}`;
  const slotLabel = `${new Date(`${day}T${time}`).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })} · ${time}`;

  const submit = async () => {
    if (!memberId) { showToast?.("Add a family member first"); return; }
    if (!address.trim()) { showToast?.("Please enter your address"); return; }
    if (!phone.trim() || phone.replace(/\D/g, "").length < 10) { showToast?.("Please enter your 10-digit phone"); return; }
    setBusy(true);
    try {
      const resp = await api.post("/bookings/home-check", {
        family_member_id: memberId,
        slot,
        address: address.trim(),
        phone: phone.replace(/\D/g, ""),
        notes: notes.trim() || "Booked via Free Essentials Check progress bar",
      });
      // WhatsApp Kirit with the booking summary (live-camp confirmation channel)
      const lines = [
        `🌱 *Mapbazar Essentials Check booked* (Free)`,
        `👤 ${titleCase(user?.name || "")} · 📞 ${phone.replace(/\D/g, "")}`,
        `❤️ For: ${titleCase(member?.name || "self")}${member?.age ? `, ${member.age}y` : ""}`,
        `🕐 ${slotLabel}`,
        `📍 ${address.trim()}`,
        `🧪 BP · Sugar · BMI · Oxygen`,
        notes.trim() ? `📝 ${notes.trim()}` : null,
        `🆔 ${resp.data?.id || ""}`,
      ].filter(Boolean);
      const waUrl = buildWhatsappLink(lines.join("\n"), config?.kirit_whatsapp);
      try { openWhatsApp(waUrl); } catch (_) {}
      showToast?.(`✓ Booked. Kirit will arrive at ${slotLabel}.`);
      onBooked?.();
    } catch (e) {
      showToast?.(e?.response?.data?.detail || "Could not book — please retry");
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="essentials-check-book-modal"
      onClick={() => !busy && onClose()}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 1300, display: "flex", alignItems: "flex-end", justifyContent: "center" }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ background: "white", borderRadius: "18px 18px 0 0", width: "100%", maxWidth: 480, maxHeight: "92dvh", overflowY: "auto", padding: 20 }}
      >
        <div style={{ width: 38, height: 4, background: "#E0DED2", borderRadius: 4, margin: "0 auto 12px" }} />
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
          <span style={{ fontSize: 22 }}>🩺</span>
          <div style={{ ...S.fraunces, fontSize: 19, fontWeight: 700, color: "#0F3D2E", flex: 1 }}>Book Free Essentials Check</div>
          <button onClick={onClose} aria-label="Close" data-testid="essentials-check-close-btn" style={{ background: "#F0EFE8", border: "none", borderRadius: "50%", width: 30, height: 30, fontSize: 14, cursor: "pointer", color: "#555" }}>✕</button>
        </div>
        <div style={{ fontSize: 12, color: "#5C6B66", marginBottom: 14, lineHeight: 1.5 }}>
          BP · Sugar · BMI · Oxygen — Kirit (pharmacist) comes to your home. Costs <strong>499 reward points</strong> from your wallet.
        </div>

        {members.length === 0 ? (
          <div data-testid="essentials-check-no-members" style={{ padding: 14, background: "#FFF8EB", border: "1px dashed #F3CC8B", borderRadius: 12, fontSize: 12.5, color: "#5C3D12", lineHeight: 1.5 }}>
            Add a family member first (from the Family tab) — the check is done for one person at a time.
          </div>
        ) : (
          <>
            <Label>For whom?</Label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              {members.map((m) => (
                <button
                  key={m.id}
                  data-testid={`essentials-check-member-${m.id}`}
                  onClick={() => setMemberId(m.id)}
                  style={chip(memberId === m.id)}
                >
                  {m.icon || "❤️"} {titleCase(m.name)}{m.age ? ` · ${m.age}` : ""}
                </button>
              ))}
            </div>

            <Label>Pick a day</Label>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {days.map((d) => (
                <button key={d} data-testid={`essentials-check-day-${d}`} onClick={() => setDay(d)} style={chip(day === d, true)}>
                  {new Date(d).toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" })}
                </button>
              ))}
            </div>

            <Label>Pick a time</Label>
            <div style={{ display: "flex", gap: 8, marginBottom: 12 }}>
              {SLOT_TIMES.map((t) => (
                <button key={t} data-testid={`essentials-check-time-${t}`} onClick={() => setTime(t)} style={chip(time === t, true)}>{t}</button>
              ))}
            </div>

            <Label>Your address</Label>
            <textarea
              data-testid="essentials-check-address"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              placeholder="House no., street, area, city"
              rows={2}
              style={fieldStyle}
            />

            <Label>Phone</Label>
            <input
              data-testid="essentials-check-phone"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              type="tel"
              inputMode="numeric"
              placeholder="10-digit mobile"
              style={fieldStyle}
            />

            <button
              data-testid="essentials-check-submit-btn"
              onClick={submit}
              disabled={busy}
              style={{
                marginTop: 14, width: "100%", padding: 14, borderRadius: 100, border: "none",
                background: busy ? "#999" : "#1D9E75", color: "white", fontSize: 14, fontWeight: 800,
                cursor: busy ? "not-allowed" : "pointer", boxShadow: busy ? "none" : "0 10px 24px rgba(29,158,117,0.4)",
              }}
            >
              {busy ? "Booking…" : `🌱 Confirm · ${slotLabel}`}
            </button>
            <div style={{ marginTop: 8, fontSize: 11, color: "#888", textAlign: "center" }}>
              We'll WhatsApp Kirit so he can confirm your slot personally.
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 11, letterSpacing: 1.2, color: "#0F3D2E", fontWeight: 700, marginBottom: 6, marginTop: 6 }}>{children}</div>;
}

const chip = (active, square = false) => ({
  padding: square ? "8px 12px" : "7px 12px",
  borderRadius: square ? 10 : 100,
  border: `1px solid ${active ? "#1D9E75" : "#D5D3C7"}`,
  background: active ? "#E8F7F0" : "white",
  color: active ? "#0F3D2E" : "#555",
  fontSize: 12, fontWeight: active ? 800 : 600, cursor: "pointer",
});

const fieldStyle = {
  width: "100%", padding: 12, borderRadius: 12,
  border: "1px solid #D5D3C7", fontSize: 13, marginBottom: 4, outline: "none",
  fontFamily: "inherit", boxSizing: "border-box",
};
